import React, { useEffect, useState } from "react";
import { Select, DatePicker } from "antd";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../store";
import { setDateRange, setRangeType } from "../store/dashboard/slice"; // Ensure correct import

const { RangePicker } = DatePicker;

interface DateRangePickerProps {
  defaultRangeType?: string;
  rangeOptions: { label: string; value: string }[];
  onRangeTypeChange: (value: string) => void;
  onDateRangeChange: (dates: [dayjs.Dayjs, dayjs.Dayjs] | null, dateStrings: [string, string]) => void;
  className?: string;
  style?: React.CSSProperties;
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  defaultRangeType = "Day",
  rangeOptions,
  onRangeTypeChange,
  onDateRangeChange,
  className = "",
  style = {},
}) => {
  const dispatch = useDispatch();
  const { dateRange, rangeType } = useSelector((state: RootState) => state.dashboard);

  // Convert dateRange from strings to dayjs objects
  const [selectedDates, setSelectedDates] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(
    dateRange ? [dayjs(dateRange[0]), dayjs(dateRange[1])] : null
  );

  const getDefaultDatesForRangeType = (type: string): [dayjs.Dayjs, dayjs.Dayjs] => {
    const now = dayjs();
    switch (type) {
      case "Year":
        return [now.startOf("year"), now.endOf("year")];
      case "Month":
        return [now.startOf("month"), now.endOf("month")];
      case "Week":
        return [now.startOf("week"), now.endOf("week")];
      case "Day":
      default:
        return [now.startOf("day"), now.endOf("day")];
    }
  };

  useEffect(() => {
    const [startTime, endTime] = getDefaultDatesForRangeType(defaultRangeType);
    setSelectedDates([startTime, endTime]);
    dispatch(setDateRange([startTime.format("YYYY-MM-DD HH:mm:ss"), endTime.format("YYYY-MM-DD HH:mm:ss")]));
    onDateRangeChange(
      [startTime, endTime],
      [startTime.format("YYYY-MM-DD HH:mm:ss"), endTime.format("YYYY-MM-DD HH:mm:ss")]
    );
  }, [dispatch, defaultRangeType]);

  const handleRangeTypeChange = (value: string) => {
    dispatch(setRangeType(value)); // Corrected Redux dispatch
    onRangeTypeChange(value);

    const [startTime, endTime] = getDefaultDatesForRangeType(value);
    setSelectedDates([startTime, endTime]);
    dispatch(setDateRange([startTime.format("YYYY-MM-DD HH:mm:ss"), endTime.format("YYYY-MM-DD HH:mm:ss")]));
    onDateRangeChange(
      [startTime, endTime],
      [startTime.format("YYYY-MM-DD HH:mm:ss"), endTime.format("YYYY-MM-DD HH:mm:ss")]
    );
  };

  const handleDateChange = (dates: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null, dateStrings: [string, string]) => {
    if (dates && dates[0] && dates[1]) {
      let [startTime, endTime] = dates;

      switch (rangeType) {
        case "Year":
          startTime = startTime.startOf("year");
          endTime = endTime.endOf("year");
          break;
        case "Month":
          startTime = startTime.startOf("month");
          endTime = endTime.endOf("month");
          break;
        case "Week":
          startTime = startTime.startOf("week");
          endTime = endTime.endOf("week");
          break;
        default:
          startTime = startTime.startOf("day");
          endTime = endTime.endOf("day");
      }

      setSelectedDates([startTime, endTime]);
      dispatch(setDateRange([startTime.format("YYYY-MM-DD HH:mm:ss"), endTime.format("YYYY-MM-DD HH:mm:ss")]));
      onDateRangeChange(
        [startTime, endTime],
        [startTime.format("YYYY-MM-DD HH:mm:ss"), endTime.format("YYYY-MM-DD HH:mm:ss")]
      );
    } else {
      setSelectedDates(null);
      dispatch(setDateRange(["", ""]));
      onDateRangeChange(null, dateStrings);
    }
  };

  return (
    <div className={`flex ${className}`} style={style}>
      <Select
        value={rangeType}
        options={rangeOptions}
        onChange={handleRangeTypeChange}
        className="week-select"
      />

      <RangePicker
        style={{ width: "320px" }}
        showTime
        value={selectedDates}
        format={
          rangeType === "Year"
            ? "YYYY"
            : rangeType === "Month"
            ? "YYYY-MM"
            : rangeType === "Week"
            ? "YYYY-wo"
            : "YYYY-MM-DD HH:mm"
        }
        picker={
          rangeType === "Year"
            ? "year"
            : rangeType === "Month"
            ? "month"
            : rangeType === "Week"
            ? "week"
            : "date"
        }
        placeholder={["From", "To"]}
        onChange={handleDateChange}
      />
    </div>
  );
};

export default DateRangePicker;
